/**
 * Tab Navigation Utility
 * Handles tab switching with support for data attributes, keyboard navigation, and ARIA
 */

class TabManager {
  constructor() {
    this.init();
  }

  init() {
    // Auto-initialize on DOM ready and Turbolinks load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializeTabs());
    } else {
      this.initializeTabs();
    }

    // Turbolinks support
    document.addEventListener('turbolinks:load', () => this.initializeTabs());
  }

  initializeTabs() {
    // Find all tab triggers with data-tab-toggle
    const tabTriggers = document.querySelectorAll('[data-tab-toggle]');
    
    tabTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.activateTab(trigger);
      });

      // Keyboard navigation
      trigger.addEventListener('keydown', (e) => {
        this.handleKeyboard(e, trigger);
      });
    });

  }

  activateTab(trigger) {
    const targetId = trigger.getAttribute('href') || trigger.getAttribute('data-tab-target');
    if (!targetId) return;

    const target = document.querySelector(targetId);
    if (!target) return;

    // Get the tab group (parent ul/nav)
    const tabList = trigger.closest('[role="tablist"]');
    if (!tabList) return;

    const previousTrigger = tabList.querySelector('[role="tab"].active');
    const previousTarget = previousTrigger ? document.querySelector(previousTrigger.getAttribute('href') || previousTrigger.getAttribute('data-tab-target')) : null;

    // Deactivate all tabs in this group
    const allTriggers = tabList.querySelectorAll('[role="tab"]');
    allTriggers.forEach(t => {
      t.setAttribute('aria-selected', 'false');
      t.classList.remove('active');
      const parent = t.closest('li');
      if (parent) parent.classList.remove('active');
    });

    // Deactivate all tab panels in this group
    const tabContent = target.closest('.tab-content, [role="tablist"] ~ div, .tab-content');
    if (tabContent) {
      const allPanels = tabContent.querySelectorAll('[role="tabpanel"]');
      allPanels.forEach(panel => {
        panel.classList.remove('active', 'block');
        panel.classList.add('hidden');
        panel.setAttribute('aria-hidden', 'true');
      });
    }

    // Activate the clicked tab
    trigger.setAttribute('aria-selected', 'true');
    trigger.classList.add('active');
    const parent = trigger.closest('li');
    if (parent) parent.classList.add('active');

    // Activate the target panel
    target.classList.add('active', 'block');
    target.classList.remove('hidden');
    target.setAttribute('aria-hidden', 'false');

    // Emit custom event
    const event = new CustomEvent('tab:shown', {
      detail: { trigger, target, previousTrigger, previousTarget },
      bubbles: true
    });
    trigger.dispatchEvent(event);
  }

  handleKeyboard(event, trigger) {
    const tabList = trigger.closest('[role="tablist"]');
    if (!tabList) return;

    const tabs = Array.from(tabList.querySelectorAll('[role="tab"]'));
    const currentIndex = tabs.indexOf(trigger);

    let nextIndex;

    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        nextIndex = currentIndex > 0 ? currentIndex - 1 : tabs.length - 1;
        tabs[nextIndex].focus();
        this.activateTab(tabs[nextIndex]);
        break;
      
      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        nextIndex = currentIndex < tabs.length - 1 ? currentIndex + 1 : 0;
        tabs[nextIndex].focus();
        this.activateTab(tabs[nextIndex]);
        break;
      
      case 'Home':
        event.preventDefault();
        tabs[0].focus();
        this.activateTab(tabs[0]);
        break;
      
      case 'End':
        event.preventDefault();
        tabs[tabs.length - 1].focus();
        this.activateTab(tabs[tabs.length - 1]);
        break;
    }
  }
}

// Initialize tab manager
const tabManager = new TabManager();

// Export for module usage if needed
if (typeof module !== 'undefined' && module.exports) {
  module.exports = TabManager;
}
